import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ArrowRight, ArrowLeft, Sparkles, Loader2, Hotel, Users, MapPin, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STEPS_AI = ["Customer Info", "Trip Details", "Package & Rates", "Review & Generate"];
const STEPS_VENDOR = ["Customer Info", "Package & Rates", "Upload Document"];

export default function ItineraryWizard() {
  const nav = useNavigate();
  const [mode, setMode] = useState("ai"); // "ai" or "vendor"
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [vendorFile, setVendorFile] = useState(null);
  const [startDate, setStartDate] = useState("");
  
  const [form, setForm] = useState({
    customer_name: "",
    customer_contact: "",
    customer_email: "",
    destination: "",
    starting_point: "New Delhi",
    days: 5,
    nights: 4,
    travel_month: "",
    travelers_adults: 2,
    travelers_children: 0,
    travelers_infants: 0,
    transport: "",
    packages: [{ name: "Standard Package", category: "3-star", hotel_name: "", transport: "", price_per_adult: "", price_per_child: "", price_per_infant: "", price_total: "", notes: "" }],
  });

  const steps = mode === "ai" ? STEPS_AI : STEPS_VENDOR;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPkg = (i, k, v) => setForm((f) => {
    const packages = [...f.packages];
    packages[i] = { ...packages[i], [k]: v };
    return { ...f, packages };
  });

  useEffect(() => {
    if (startDate) {
      const startD = new Date(startDate);
      const endD = new Date(startD);
      endD.setDate(endD.getDate() + Math.max(0, form.days - 1));
      const options = { day: 'numeric', month: 'short', year: 'numeric' };
      const formattedStr = `${startD.toLocaleDateString('en-US', options)} to ${endD.toLocaleDateString('en-US', options)}`;
      if (form.travel_month !== formattedStr) {
        set("travel_month", formattedStr);
      }
    } else {
      if (form.travel_month !== "") {
        set("travel_month", "");
      }
    }
  }, [startDate, form.days, form.travel_month]);

  const addPkg = () => setForm((f) => ({ ...f, packages: [...f.packages, { name: "", category: "", hotel_name: "", transport: "", price_per_adult: "", price_per_child: "", price_per_infant: "", price_total: "", notes: "" }] }));
  const removePkg = (i) => setForm((f) => ({ ...f, packages: f.packages.filter((_, idx) => idx !== i) }));

  const autoCalcPackageTotal = (i) => {
    setForm((f) => {
      const packages = [...f.packages];
      const p = packages[i];
      const ad = Number(p.price_per_adult || 0) * Number(f.travelers_adults || 0);
      const ch = Number(p.price_per_child || 0) * Number(f.travelers_children || 0);
      const inf = Number(p.price_per_infant || 0) * Number(f.travelers_infants || 0);
      packages[i] = { ...p, price_total: ad + ch + inf };
      return { ...f, packages };
    });
  };

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const validateStep = () => {
    if (step === 0 && !form.customer_name) return "Customer name required";
    if (mode === "ai" && step === 1) {
      if (!form.destination) return "Destination required";
      if (!form.days || form.days < 1) return "Valid days required";
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) return toast.error(err);
    next();
  };

  const getCleanPackages = () => {
    return form.packages
      .filter((p) => p.name)
      .map((p) => ({
        ...p,
        price_per_adult: p.price_per_adult ? Number(p.price_per_adult) : null,
        price_per_child: p.price_per_child ? Number(p.price_per_child) : null,
        price_per_infant: p.price_per_infant ? Number(p.price_per_infant) : null,
        price_total: p.price_total ? Number(p.price_total) : null,
        hotel_name: p.hotel_name || null,
        transport: p.transport || null,
      }));
  };

  const generateAI = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        packages: getCleanPackages(),
        days: Number(form.days),
        nights: Number(form.nights),
        travelers_adults: Number(form.travelers_adults),
        travelers_children: Number(form.travelers_children),
        travelers_infants: Number(form.travelers_infants),
      };
      const res = await api.generateItinerary(payload);
      toast.success("Itinerary generated! Opening editor…");
      setTimeout(() => nav(`/admin/itineraries/${res.id}`), 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
      setLoading(false);
    }
  };

  const generateVendor = async () => {
    if (!vendorFile) return toast.error("Please upload a vendor itinerary document.");
    if (vendorFile.size > 100 * 1024 * 1024) return toast.error("File size exceeds the 100MB limit. Please compress your PDF and try again.");
    setLoading(true);
    try {
      const customerData = {
        customer_name: form.customer_name,
        customer_contact: form.customer_contact,
        customer_email: form.customer_email,
        travelers_adults: Number(form.travelers_adults),
        travelers_children: Number(form.travelers_children),
        travelers_infants: Number(form.travelers_infants),
        packages: getCleanPackages(),
      };
      const res = await api.parseVendorItinerary(vendorFile, customerData);
      toast.success("Vendor itinerary parsed successfully! Opening editor…");
      setTimeout(() => nav(`/admin/itineraries/${res.id}`), 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to parse vendor itinerary.");
      setLoading(false);
    }
  };

  const totalTravelers = Number(form.travelers_adults) + Number(form.travelers_children) + Number(form.travelers_infants);

  // Helper to render steps depending on mode
  const renderStepContent = () => {
    // Step 0: Customer Info is always the same
    if (step === 0) {
      return (
        <div className="space-y-4">
          <h2 className="font-display font-semibold text-slate-800 text-lg mb-4">👤 Customer Details</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-800">Customer Name *</Label>
              <Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="e.g., Rohit Sharma" className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="wizard-customer-name-input" />
            </div>
            <div>
              <Label className="text-slate-800">Phone</Label>
              <Input value={form.customer_contact} onChange={(e) => set("customer_contact", e.target.value)} placeholder="+91 XXXXX XXXXX" className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="wizard-customer-phone-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-800">Email</Label>
              <Input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" placeholder="customer@email.com" data-testid="wizard-customer-email-input" />
            </div>
          </div>

          <div className="border-t border-slate-200 pt-5 mt-4">
            <h2 className="font-display font-semibold text-slate-800 text-lg mb-1 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" /> Travelers
              <Badge className="bg-orange-100 text-orange-700 border-orange-200 ml-2 hover:bg-orange-200">{totalTravelers} Total</Badge>
            </h2>
            <p className="text-xs text-slate-500 mb-4">Required for package price calculation.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-blue-500/10 rounded-2xl p-4 border border-blue-500/20">
                <Label className="text-orange-500 font-semibold flex items-center gap-1 mb-2">👨‍👩‍👦 Adults</Label>
                <Input type="number" min={1} value={form.travelers_adults} onChange={(e) => set("travelers_adults", Number(e.target.value))} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 text-lg font-bold text-center" />
              </div>
              <div className="bg-green-500/10 rounded-2xl p-4 border border-green-500/20">
                <Label className="text-green-400 font-semibold flex items-center gap-1 mb-2">🧒 Children</Label>
                <Input type="number" min={0} value={form.travelers_children} onChange={(e) => set("travelers_children", Number(e.target.value))} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 text-lg font-bold text-center" />
              </div>
              <div className="bg-pink-500/10 rounded-2xl p-4 border border-pink-500/20">
                <Label className="text-pink-400 font-semibold flex items-center gap-1 mb-2">👶 Infants</Label>
                <Input type="number" min={0} value={form.travelers_infants} onChange={(e) => set("travelers_infants", Number(e.target.value))} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 text-lg font-bold text-center" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (mode === "ai" && step === 1) {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="font-display font-semibold text-slate-800 text-lg mb-4">✈️ Trip Details</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-800">Destination *</Label>
                <Input value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="e.g., Char Dham Yatra / Bali" className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="wizard-destination-input" />
              </div>
              <div>
                <Label className="text-slate-800">Starting Point</Label>
                <Input value={form.starting_point} onChange={(e) => set("starting_point", e.target.value)} placeholder="e.g., New Delhi" className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="wizard-start-input" />
              </div>
              <div>
                <Label className="text-slate-800">Days *</Label>
                <Input type="number" min={1} max={45} value={form.days} onChange={(e) => { const v = Number(e.target.value); set("days", v); set("nights", Math.max(0, v - 1)); }} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="wizard-days-input" />
              </div>
              <div>
                <Label className="text-slate-800">Nights</Label>
                <Input type="number" min={0} value={form.nights} onChange={(e) => set("nights", Number(e.target.value))} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="wizard-nights-input" />
              </div>
              <div>
                <Label className="text-slate-800">Travel Start Date</Label>
                <Input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => {
                    const startStr = e.target.value;
                    setStartDate(startStr);
                    if (!startStr) {
                      set("travel_month", "");
                      return;
                    }
                    const startD = new Date(startStr);
                    const endD = new Date(startD);
                    endD.setDate(endD.getDate() + Math.max(0, form.days - 1));
                    const options = { day: 'numeric', month: 'short', year: 'numeric' };
                    set("travel_month", `${startD.toLocaleDateString('en-US', options)} to ${endD.toLocaleDateString('en-US', options)}`);
                  }}
                  className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" 
                />
                {form.travel_month && <p className="text-xs text-slate-500 mt-1">Calculated: {form.travel_month}</p>}
              </div>
              <div>
                <Label className="text-slate-800">Transport Mode</Label>
                <Select value={form.transport} onValueChange={(v) => set("transport", v)}>
                  <SelectTrigger className="rounded-xl bg-slate-50 border-slate-200 text-slate-800"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    <SelectItem value="Flight">Flight</SelectItem>
                    <SelectItem value="Train">Train</SelectItem>
                    <SelectItem value="Sedan Car">Sedan Car</SelectItem>
                    <SelectItem value="SUV / Innova">SUV / Innova</SelectItem>
                    <SelectItem value="Volvo Bus">Volvo Bus</SelectItem>
                    <SelectItem value="Self Drive">Self Drive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if ((mode === "ai" && step === 2) || (mode === "vendor" && step === 1)) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="font-display font-semibold text-slate-800 text-lg">💰 Package Options & Rates</h2>
              <p className="text-xs text-slate-500 mt-0.5">Add one or more pricing tiers (3-star, 4-star, luxury etc.). These will appear in the final itinerary PDF.</p>
            </div>
            <Button size="sm" variant="outline" onClick={addPkg} className="rounded-xl border-slate-200 text-slate-800 hover:bg-slate-100"><Plus className="w-3.5 h-3.5 mr-1" /> Add Package</Button>
          </div>
          <div className="space-y-3">
            {form.packages.map((p, i) => (
              <Card key={i} className="p-4 rounded-xl bg-slate-50 border-slate-200">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center mb-3">
                  <Input placeholder="Package Name" value={p.name || ""} onChange={(e) => setPkg(i, "name", e.target.value)} className="sm:col-span-11 rounded-lg bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" />
                  <Button variant="ghost" size="icon" onClick={() => removePkg(i)} className="sm:col-span-1 justify-self-start sm:justify-self-end mt-2 sm:mt-0"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                </div>
                <div className="mb-3 px-1">
                  <Input placeholder="Notes / Exclusions" value={p.notes || ""} onChange={(e) => setPkg(i, "notes", e.target.value)} className="rounded-lg w-full bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-start bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                  <div>
                    <Label className="text-[10px] text-slate-500 mb-1 block font-semibold uppercase tracking-wider">Per Adult (₹)</Label>
                    <Input type="number" placeholder="0" value={p.price_per_adult || ""} onChange={(e) => setPkg(i, "price_per_adult", e.target.value)} className="rounded-lg text-sm bg-white/50 border-slate-200 text-slate-800" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 mb-1 block font-semibold uppercase tracking-wider">Per Child (₹)</Label>
                    <Input type="number" placeholder="0" value={p.price_per_child || ""} onChange={(e) => setPkg(i, "price_per_child", e.target.value)} className="rounded-lg text-sm bg-white/50 border-slate-200 text-slate-800" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 mb-1 block font-semibold uppercase tracking-wider">Per Infant (₹)</Label>
                    <Input type="number" placeholder="0" value={p.price_per_infant || ""} onChange={(e) => setPkg(i, "price_per_infant", e.target.value)} className="rounded-lg text-sm bg-white/50 border-slate-200 text-slate-800" />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label className="text-[10px] text-orange-500 font-bold uppercase tracking-wider">Total (₹)</Label>
                      <button className="text-[10px] text-orange-500 hover:underline hover:text-orange-700 font-semibold cursor-pointer" onClick={(e) => { e.preventDefault(); autoCalcPackageTotal(i); }}>Auto-Calc</button>
                    </div>
                    <Input type="number" placeholder="0" value={p.price_total || ""} onChange={(e) => setPkg(i, "price_total", e.target.value)} className="rounded-lg text-sm bg-orange-50 border-orange-200 font-bold text-slate-800" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    if (mode === "ai" && step === 3) {
      return (
        <div>
          <h2 className="font-display font-semibold text-slate-800 text-lg mb-4">✅ Review & Generate</h2>
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            <Info label="Customer" value={form.customer_name} />
            <Info label="Destination" value={form.destination} />
            <Info label="Duration" value={`${form.days}D / ${form.nights}N`} />
            <Info label="Packages" value={`${form.packages.filter(p => p.name).length} package(s) configured`} />
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Card className="p-4 rounded-xl bg-blue-500/10 border-blue-500/20">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1D4ED8] to-[#0EA5E9] flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4 text-slate-800" /></div>
                <div>
                  <div className="font-display font-semibold text-slate-800 text-sm">AI Itinerary Generation</div>
                  <p className="text-xs text-slate-500 mt-1">Gemini AI will craft a {form.days}-day route with vivid day-descriptions.</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      );
    }

    if (mode === "vendor" && step === 2) {
      return (
        <div className="space-y-4">
          <h2 className="font-display font-semibold text-slate-800 text-lg mb-4">📄 Upload Vendor Itinerary</h2>
          <Card className="p-8 border-dashed border-2 border-white/20 rounded-xl bg-slate-50 text-center">
            <UploadCloud className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <div className="mb-4">
              <Label htmlFor="vendor-upload" className="cursor-pointer text-orange-500 font-semibold hover:underline">Click to browse</Label>
              <span className="text-slate-500 text-sm ml-1">or drag and drop your document here</span>
              <Input id="vendor-upload" type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,image/*" onChange={(e) => setVendorFile(e.target.files[0])} />
            </div>
            {vendorFile && (
              <Badge className="px-3 py-1 text-sm bg-blue-500/20 text-orange-500 border border-blue-500/30">
                {vendorFile.name}
              </Badge>
            )}
            <p className="text-xs text-slate-500 mt-4">Accepts PDF, Word, Text, or Images. AI will read it and structure it to fit your editor and branding.</p>
          </Card>
        </div>
      );
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-800">New Itinerary</h1>
          <p className="text-slate-500">Create from scratch using AI or upload a vendor's document.</p>
        </div>
        
        {/* Toggle Mode */}
        <div className="flex bg-white/60 backdrop-blur-sm p-1.5 rounded-xl border border-slate-200 shadow-inner w-fit gap-1">
          <button 
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === 'ai' ? 'bg-[#054A4D] text-white shadow-md' : 'text-slate-600 hover:bg-slate-100 hover:text-[#054A4D]'}`}
            onClick={() => { setMode('ai'); setStep(0); }}
          >
            Create with AI
          </button>
          <button 
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === 'vendor' ? 'bg-[#054A4D] text-white shadow-md' : 'text-slate-600 hover:bg-slate-100 hover:text-[#054A4D]'}`}
            onClick={() => { setMode('vendor'); setStep(0); }}
          >
            Upload Vendor PDF
          </button>
        </div>
      </div>

      {/* Stepper */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          {steps.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`text-xs font-medium ${i === step ? "text-orange-500" : i < step ? "text-green-400" : "text-slate-500"}`}>
                Step {i + 1} · {s}
              </div>
            </div>
          ))}
        </div>
        <Progress value={((step + 1) / steps.length) * 100} className="h-2" />
      </div>

      <Card className="p-6 lg:p-8 rounded-2xl glass-panel-dark border-slate-200">
        {renderStepContent()}

        {/* Footer */}
        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" onClick={back} disabled={step === 0 || loading} className="rounded-xl border-slate-200 text-slate-800 hover:bg-slate-100 hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
          
          {step < steps.length - 1 ? (
            <Button onClick={handleNext} className="rounded-xl btn-luxe  text-white">Next <ArrowRight className="w-4 h-4 ml-2" /></Button>
          ) : (
            <Button onClick={mode === "ai" ? generateAI : generateVendor} disabled={loading} className="rounded-xl bg-gradient-to-r from-[#1D4ED8] to-[#0EA5E9] text-slate-800 hover:opacity-95">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin text-slate-800" /> {mode === "ai" ? "Generating" : "Parsing"}… (30-60s)</> : <><Sparkles className="w-4 h-4 mr-2" /> {mode === "ai" ? "Generate with AI" : "Parse Vendor File"}</>}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-800">{value}</span>
    </div>
  );
}
